import { createSlice } from '@reduxjs/toolkit';
import { showToast } from '@global/design-system/molecules';
import fileDownload from 'js-file-download';
import { GenericEntityState, ResponseAction } from '../types';
import createFetchReducer from '@app/utils/createFetchReducer';
import { normalize } from 'normalizr';
import * as R from 'ramda';
import { CtaHiddenUpdate } from '../cta/types';
import {
	filterCampaignsTypes,
	updateCampaignTypes,
	launchDraftCampaignTypes,
	exportCampaignsTypes,
	exportSingleCampaignTypes,
	addContactsToCampaignTypes,
	emailPreviewTypes,
	resendStoriesCounttypes,
	resendCampaignStoriesTypes,
	getCampaignOptionsTypes,
	getCampaignTypes,
	getCampaignsCountTypes,
	saveCampaignTypes,
	launchCampaignTypes,
	saveCurrentChildCampaignTypes,
	saveAndUpdateCurrentChildCampaignTypes,
	deactivateCampaignTypes,
	getEmailTemplatesTypes,
	duplicateCampaignTypes,
	bulkDeleteDraftCampaignsTypes,
	getVerifiedStorytellersTypes,
	pinCampaignTypes,
	getEmailTemplateHTMLTypes,
	getHotglueJobTypes,
	getCampaignPerCTAType,
	getCTAsInCampaignsTypes,
	editEmailTemplateTypes,
	acceptPendingCampaignContactsTypes,
	approvePendingStoriesTypes,
	setLiveCampaignTypes,
	updateCampaignV2Types,
	updateCampaignStorytellersTypes,
	rejectPendingStoriesTypes,
	validateRepeatedNameTypes,
	deletePendingStoriesTypes,
	getPendingStoriesTypes,
	getAssignedStoriesTypes,
	updateCampaignEmailFieldsTypes,
	updateNicheInboxContentTextTypes,
	activateCampaignTypes,
} from './action';
import entityNormalizer from '@app/utils/entityNormalizer';
import objectToOptions from '@app/utils/ObjectToOptions';

import { ToastMessage } from '@app/constants';
import { recipesStateType } from '../recipe';
import { CampaignStateEnum, CampaignType } from './types';
import { setStatusChangeInCampaigns } from './utils';

import { FIRST_PAGE } from '../constants';
import { capitalize } from '@app/utils/formatString';
import {
	CANNOT_ACTIVATE_CAMPAIGN_FOR_NON_PREMIUM_ENTITY,
	CANNOT_ACTIVATE_CAMPAIGN_WITH_ALL_CTAS_ARCHIVED,
	EXPIRATION_DATE_BEFORE_SCHEDULE_DATE_ERROR_CODE,
	REPEATED_CAMPAIGN_NAME_ERROR_CODE,
} from '@app/constants/error';

const campaign = entityNormalizer('campaigns', {}, { idAttribute: '_id' });
const ctas = entityNormalizer('ctas', {}, { idAttribute: '_id' });

const pagination = { campaigns: [campaign] };
const ctaPagination = { ctas: [ctas] };

// add pagination shouldCallAPI only when the filterObject string is equals to the currentFilter (store currentfilter on each api call )
export const initialState: GenericEntityState = {
	loading: false,
	campaign: {},
	campaigns: {},
	recentlyLaunchedCampaigns: [],
	campaignCount: 0,
	limitHitCampaignCount: 0,
	pagination: {
		pages: {},
		currentPage: 1,
		totalPages: null,
		totalTabCount: null,
	},
	paginationOptions: {
		pages: {},
		currentPage: 1,
		totalPages: null,
	},
	error: {},
	response: {
		status: null,
		message: null,
	},
	updateResponse: {
		status: null,
		message: null,
	},
	launchResponse: {
		status: null,
		message: null,
	},
	allPendingStoriesList: [],
	allPendingStoriesPagination: {
		currentPage: FIRST_PAGE,
		totalPages: null,
		count: 0,
	},
	pendingStoriesByCampaign: [],
	pendingStoriesPaginationByCampaign: {
		currentPage: FIRST_PAGE,
		totalPages: null,
		count: 0,
	},
	assignedStoriesByCampaign: [],
	assignedStoriesPaginationByCampaign: {
		currentPage: FIRST_PAGE,
		totalPages: null,
		count: 0,
	},
	custom: {},
	campaignOptions: [],
	savedCampaign: {},
	campaignTree: {},
	campaignName: 'Untitled Campaign',
	totalCampaigns: {},
	verifiedStorytellers: [],
	activeCtas: {
		activeCTACount: 0,
		activeCTAs: {},
		activeCTAPagination: {
			currentPage: 1,
			totalPages: null,
			count: null,
		},
	},
};

export type campaignsStateType = typeof initialState;

function normalizeResponse(action: ResponseAction) {
	const {
		response: {
			campaigns,
			page = null,
			pageSize = null,
			totalPages = null,
			count = null,
		},
	} = action;
	const normalizedData = normalize(
		{ campaigns, page, pageSize, totalPages, count },
		pagination,
	);
	return normalizedData;
}

function paginationMapper(state: campaignsStateType, action: ResponseAction) {
	const normalizedData = normalizeResponse(action);

	// to make the infinite scroll works
	if (action.response.page === FIRST_PAGE) {
		state.campaigns = { ...normalizedData.entities.campaigns };
	} else {
		state.campaigns = {
			...state.campaigns,
			...normalizedData.entities.campaigns,
		};
	}
	state.limitHitCampaignCount = action?.response?.limitHitCampaignCount;
	state.campaignCount = Object.keys(state.campaigns || {})?.length;
	// Options for the dropdown menu
	if (state.pagination) {
		state.pagination.pages[action.response.page] =
			normalizedData.result.campaigns;
		state.pagination.currentPage = action.response.page;
		state.pagination.totalPages = action.response.totalPages;
		state.pagination.count = action.response.count;
		state.pagination.totalTabCount = action.response.totalTabCount;
	}
}

function activeCTAsPaginationMapper(
	state: campaignsStateType,
	action: ResponseAction,
) {
	const { count, page, pageSize, totalPages, ctas } = action.response.result;
	const normalizedActiveCTAs = normalize(
		{ count, page, pageSize, totalPages, ctas },
		ctaPagination,
	);

	if (action.response.result.page === FIRST_PAGE) {
		state.activeCtas.activeCTAs = { ...normalizedActiveCTAs.entities.ctas };
	} else {
		state.activeCtas.activeCTAs = {
			...state.activeCtas.activeCTAs,
			...normalizedActiveCTAs.entities.ctas,
		};
	}

	state.activeCtas.activeCTACount = Object.keys(
		state.activeCtas.activeCTAs || {},
	)?.length;
	state.activeCtas.activeCTAPagination.currentPage =
		action.response.result.page;
	state.activeCtas.activeCTAPagination.totalPages =
		action.response.result.totalPages;
	state.activeCtas.activeCTAPagination.count = action.response.result.count;
}

function getCampaign(state: campaignsStateType, action: ResponseAction) {
	const { campaign, sharedVideoStats, contactsByPreference } = action.response;
	state.campaign = {
		...campaign,
		sharedVideoStats,
		contactsByPreference,
	};

	//If savedCampaign exists with the same id then deep merge campaign to savedCampaign

	if (state.savedCampaign?._id === campaign._id) {
		state.savedCampaign = R.mergeDeepRight(state.savedCampaign, campaign);
	}
}

function optionsMapper(state: campaignsStateType, action: ResponseAction) {
	const normalizedData: any = normalizeResponse(action);
	const campaignCopy = objectToOptions(
		normalizedData.entities.campaigns,
		'id',
		'name',
	);
	if (!action.payload?.excludeAll && !action.payload.includeBoth) {
		campaignCopy.unshift({
			value: '',
			label: 'All',
		});
	}

	if (action?.response?.page > 1) {
		state.campaignOptions = [...state.campaignOptions, ...campaignCopy];
	} else {
		state.campaignOptions = campaignCopy;
	}
	if (action.payload.includeBoth) {
		state.campaignOptions = campaignCopy;
		state.campaignOptionsAll = campaignCopy;
		state.campaignOptionsAll = [
			{
				value: '',
				label: 'All',
			},
			...campaignCopy,
		];
	}
	state.paginationOptions.pages[action.response.page] =
		normalizedData.result.campaigns;
	state.paginationOptions.currentPage = action.response.page;
	state.paginationOptions.totalPages = action.response.totalPages;
	state.paginationOptions.count = action.response.count;
}

function getCampaignsCountMapper(
	state: campaignsStateType,
	action: ResponseAction,
) {
	state.totalCampaigns = action.response.campaigns;
}

function updateCampaignMapper(
	state: campaignsStateType,
	action: ResponseAction,
) {
	state.campaign = { ...state.campaign, ...action.response.campaign };
	state.updateResponse = action.response;

	if (action.payload?.removeFromCampaignsList) {
		//TODO: Fix this state mutation
		// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
		delete state.campaigns[action.response.campaign._id];
	}
}

function updateCampaignErrorMapper(
	state: campaignsStateType,
	action: ResponseAction,
) {
	const errorCode = action?.error?.response?.data?.errorCode;

	if (errorCode === CANNOT_ACTIVATE_CAMPAIGN_WITH_ALL_CTAS_ARCHIVED) {
		return showToast({
			message: ToastMessage.cannotActivateCampaignWithAllCtasArchived,
			type: 'error',
		});
	} else if (errorCode === CANNOT_ACTIVATE_CAMPAIGN_FOR_NON_PREMIUM_ENTITY) {
		return showToast({
			message: ToastMessage.cannotActivateCampaignForNonPremiumEntity,
			type: 'error',
		});
	}

	return showToast({ message: ToastMessage.error, type: 'error' });
}

function pinCampaignMapper(state: campaignsStateType, action: ResponseAction) {
	const { _id, pinned } = action.response?.campaign;

	if (state.campaignCount === 0 && !state.campaigns[_id]) return;

	state.campaigns[_id].pinned = pinned;

	if (state.campaign?._id === _id) state.campaign.pinned = pinned;
}

function deactivateCampaignMapper(
	state: campaignsStateType,
	action: ResponseAction,
) {
	const deactivatedCampaign = action.response.campaign;
	state.campaign.state = deactivatedCampaign.state;
	state.campaign.pendingStories = deactivatedCampaign.pendingStories;
	state.campaign.pendingStoriesCount = deactivatedCampaign.pendingStoriesCount;
	state.campaign.assignedStories = deactivatedCampaign.assignedStories;
	state.campaign.assignedStoriesCount =
		deactivatedCampaign.assignedStoriesCount;

	//Remove campaign from state.campaigns

	//TODO: Fix this state mutation
	// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
	delete state.campaigns[deactivatedCampaign._id];
}

function activateCampaignMapper(
	state: campaignsStateType,
	action: ResponseAction,
) {
	const activatedCampaign = action.response.campaign;

	//Remove campaign from state.campaigns
	//TODO: Fix this state mutation
	// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
	delete state.campaigns[activatedCampaign._id];
}

function bulkDeleteDraftCampaignsMapper(
	state: campaignsStateType,
	action: ResponseAction,
) {
	const { campaignIds } = action.response;

	const result = campaignIds.reduce(
		(objToReturn: any, id: string) => {
			const { [id]: _deletedCampaign, ...rest } = objToReturn;
			return { ...rest };
		},
		{ ...state.campaigns },
	);

	state.campaigns = result;

	const newCampaignsCount = state.campaignCount - campaignIds.length;
	state.campaignCount = newCampaignsCount > 0 ? newCampaignsCount : 0;
}

function launchDraftCampaignMapper(
	state: campaignsStateType,
	action: ResponseAction,
) {
	state.launchResponse = action.response;
	state.launchResponse.error = action.error;
}

export const exportCampaignsSuccessMapper = (
	state: campaignsStateType,
	action: ResponseAction,
) => {
	const { response } = action;
	fileDownload(response, 'campaigns.csv');
	state.type = action.type;
};

export const exportSingleCampaignSuccessMapper = (
	state: campaignsStateType,
	action: ResponseAction,
) => {
	const { response } = action;
	const { campaignName } = action.payload;
	fileDownload(response, `${campaignName}.csv`);
	state.type = action.type;
};

function addContactsToCampaignErrorMapper(
	state: recipesStateType,
	action: ResponseAction,
) {
	const errorCode = action?.error?.response?.data?.errorCode;

	const isNoValidContactsInboxCampaign = 103;
	const isCollectCampaign = 31;

	if (errorCode === 60) {
		return showToast({
			message: ToastMessage.overApprovedContactLimit,
			type: 'error',
		});
	}

	if (errorCode === isCollectCampaign) {
		return showToast({
			message: ToastMessage.assignContactsToCollectVideoCampaignError,
			type: 'error',
		});
	}

	if (errorCode === isNoValidContactsInboxCampaign) {
		return showToast({
			message: ToastMessage.assignContactsInboxCampaignError,
			type: 'error',
		});
	}

	return showToast({
		message: ToastMessage.contactsToCampaignError,
		type: 'error',
	});
}

function updateTreeCampaign(state: recipesStateType, action: ResponseAction) {
	if (!!state.campaignTree[action?.response?.campaign?._id]) {
		state.campaignTree = {
			...state.campaignTree,
			[action.response.campaign._id]: action.response.campaign,
		};
	}
}

function saveCampaignMapper(state: recipesStateType, action: ResponseAction) {
	updateTreeCampaign(state, action);
	state.savedCampaign = {
		...state.savedCampaign,
		...action?.response?.campaign,
		stories: action?.response?.stories ?? state.savedCampaign.stories,
		verifiedStorytellers: [],
	};
	state.campaignName = action?.response?.campaign.name;
}

function normalizeCampaignTree(campaigns: CampaignType[]) {
	const entityNormalizer = normalize({ campaigns }, pagination);
	return { ...entityNormalizer.entities.campaigns };
}

function saveCurrentChildCampaignMapper(
	state: recipesStateType,
	action: ResponseAction,
) {
	updateTreeCampaign(state, action);
}

function launchCampaignMapper(state: recipesStateType, action: ResponseAction) {
	const currentBuildingCampaign = state.savedCampaign;
	const campaignTree = state.campaignTree;
	const addCampaignToList = action?.payload?.addToCampaignsList;

	// `campaigns` is a single campaign object for the common single-launch flow, or an
	// array of campaigns for the bulk (funnel) launch. Normalize to an array.
	const launchedResponse: CampaignType[] | CampaignType =
		action?.response?.campaigns;
	const launchedCampaigns = (
		Array.isArray(launchedResponse) ? launchedResponse : [launchedResponse]
	)
		.filter(Boolean)
		// Ensure the campaign has an id in the 'id' format
		.map((campaign) => ({ ...campaign, id: campaign.id || campaign._id }));

	if (addCampaignToList) {
		// Optimistically surface the just-launched campaigns at the top of the list.
		const launchedById = launchedCampaigns.reduce(
			(acc, campaign) => ({ ...acc, [campaign._id]: campaign }),
			{},
		);
		state.campaigns = { ...launchedById, ...state.campaigns };
	}

	// Buffer the launched campaigns so the list can re-surface them as "ghost" rows for non-optimistic add case
	state.recentlyLaunchedCampaigns = launchedCampaigns;

	state.savedCampaign = {
		...state.savedCampaign,
		state: CampaignStateEnum.active,
	};

	//TODO: This is deprecated code, "campaignTree" is not used anymore, not removed because we need to make a major clean up related to this.
	if (campaignTree[currentBuildingCampaign?._id]) {
		const campaignTreeUpdated = setStatusChangeInCampaigns(
			campaignTree,
			CampaignStateEnum.active,
		);

		state.campaignTree = normalizeCampaignTree(campaignTreeUpdated);
	}
}

function launchCampaignErrorMapper(
	state: recipesStateType,
	action: ResponseAction,
) {
	const errorMessage = action?.error?.response?.data?.message;
	const errorCode = action?.error?.response?.data?.errorCode;
	const haveMaxNumberOfContacts =
		errorMessage?.includes('Maximun number of contacts exceed.') ||
		errorMessage?.includes('Maximum number of contacts exceed.');
	const videoIsPendingApproval = errorCode === 16;
	const templateIsNotAssignedToTeam = errorCode === 2202;
	const workspacePlanIsNotEnabled = errorCode === 514;
	const isExpirationDateBeforeSchedule =
		errorCode === EXPIRATION_DATE_BEFORE_SCHEDULE_DATE_ERROR_CODE;

	if (templateIsNotAssignedToTeam) {
		return showToast({
			message: ToastMessage.templateNotAssignedToTeamError,
			type: 'error',
		});
	}

	if (workspacePlanIsNotEnabled) {
		const plan = action?.error?.response?.data?.metadata?.plan;
		return showToast({
			message: `${capitalize(plan)} plan is not enabled for this workspace yet.`,
			type: 'error',
		});
	}
	if (haveMaxNumberOfContacts) {
		return showToast({
			message: ToastMessage.audienceMaxContactError,
			type: 'error',
		});
	} else if (videoIsPendingApproval) {
		return showToast({
			message: ToastMessage.videoIsPendingApproval,
			type: 'error',
		});
	} else if (isExpirationDateBeforeSchedule) {
		return showToast({
			message: ToastMessage.campaignExpirationDateBeforeSendingError,
			type: 'error',
		});
	} else {
		return showToast({ message: ToastMessage.error, type: 'error' });
	}
}

function emailPreviewErrorMapper(
	state: recipesStateType,
	action: ResponseAction,
) {
	const errorResponse = action?.error?.response;
	const errorMessage = errorResponse?.data?.message || errorResponse?.data;

	const isCtaNotDefined = errorMessage?.includes('cta must be defined');
	const isPhoneNumberInvalid = errorMessage?.includes(
		'is not a valid phone number',
	);

	if (isCtaNotDefined) {
		return showToast({ message: ToastMessage.ctaNotSelected, type: 'error' });
	} else if (isPhoneNumberInvalid) {
		return showToast({
			message: ToastMessage.invalidPhoneNumber,
			type: 'error',
		});
	} else {
		return showToast({ message: ToastMessage.error, type: 'error' });
	}
}

function saveCampaignErrorMapper(
	state: recipesStateType,
	action: ResponseAction,
) {
	const errorCode = action?.error?.response?.data?.errorCode;
	const errorMessage = action?.error?.response?.data?.message;

	const campaignNotFound = errorCode === 5;

	const haveMaxNumberOfContacts =
		errorMessage?.includes('Maximun number of contacts exceed.') ||
		errorMessage?.includes('Maximum number of contacts exceed.');

	const IsMissingFallbackCampaign =
		campaignNotFound && errorMessage?.includes('Campaign not found');

	const isExpirationDateBeforeSchedule =
		errorCode === EXPIRATION_DATE_BEFORE_SCHEDULE_DATE_ERROR_CODE;

	const hasRepeatedCampaignName =
		errorCode === REPEATED_CAMPAIGN_NAME_ERROR_CODE;

	if (haveMaxNumberOfContacts) {
		return showToast({
			message: ToastMessage.audienceMaxContactError,
			type: 'error',
		});
	}

	if (IsMissingFallbackCampaign) {
		return showToast({
			message: ToastMessage.fallbackCampaignMissing,
			type: 'error',
		});
	}

	if (isExpirationDateBeforeSchedule) {
		return showToast({
			message: ToastMessage.campaignExpirationDateBeforeSendingError,
			type: 'error',
		});
	}

	if (hasRepeatedCampaignName) {
		return showToast({
			message: ToastMessage.campaignNameAlreadyExists,
			type: 'error',
		});
	}

	return showToast({ message: ToastMessage.error, type: 'error' });
}

function getPendingStoriesMapper(
	state: campaignsStateType,
	action: ResponseAction,
) {
	const {
		response: { stories, count, totalPages, page },
		payload: { byCampaign },
	} = action;
	if (byCampaign) {
		if (page === FIRST_PAGE) {
			state.pendingStoriesByCampaign = stories;
		} else {
			state.pendingStoriesByCampaign = [
				...state.pendingStoriesByCampaign,
				...stories,
			];
		}
		state.pendingStoriesPaginationByCampaign.currentPage = page;
		state.pendingStoriesPaginationByCampaign.totalPages = totalPages;
		state.pendingStoriesPaginationByCampaign.count = count;
	} else {
		if (page === FIRST_PAGE) {
			state.allPendingStoriesList = stories;
		} else {
			state.allPendingStoriesList = [
				...state.allPendingStoriesList,
				...stories,
			];
		}
		state.allPendingStoriesPagination.currentPage = page;
		state.allPendingStoriesPagination.totalPages = totalPages;
		state.allPendingStoriesPagination.count = count;
	}
}

function getAssignedStoriesMapper(
	state: campaignsStateType,
	action: ResponseAction,
) {
	const {
		response: { stories, count, totalPages, page },
	} = action;
	if (page === FIRST_PAGE) {
		state.assignedStoriesByCampaign = stories;
	} else {
		state.assignedStoriesByCampaign = [
			...state.assignedStoriesByCampaign,
			...stories,
		];
	}
	state.assignedStoriesPaginationByCampaign.currentPage = page;
	state.assignedStoriesPaginationByCampaign.totalPages = totalPages;
	state.assignedStoriesPaginationByCampaign.count = count;
}

function approvePendingStoriesMapper(
	state: campaignsStateType,
	action: ResponseAction,
) {
	const {
		response: { campaigns, stories },
		payload: { campaignToUpdate, campaignReportsPage, bulkAction },
	} = action;

	// Use the first campaign for campaignReportsPage
	if (campaignReportsPage && campaigns && campaigns.length > 0) {
		const campaign = campaigns[0];
		state.campaign = {
			...state.campaign,
			storiesPendingApproval: campaignToUpdate.storiesPendingApproval,
			storiesPendingApprovalCount: campaign.storiesPendingApprovalCount,
			actionsRequired: campaign.actionsRequired,
		};
	}

	// Define a clear boolean variable for the condition
	const shouldUpdateAllCampaigns =
		!campaignReportsPage && campaigns && campaigns.length > 0;

	if (shouldUpdateAllCampaigns) {
		campaigns.forEach((currentCampaign: any) => {
			state.campaigns[currentCampaign._id] = {
				...state.campaigns[currentCampaign._id],
				actionsRequired: currentCampaign.actionsRequired,
				assignedStoriesCount: currentCampaign.assignedStoriesCount,
				completedStoriesCount: currentCampaign.completedStoriesCount,
				isLive: currentCampaign.isLive,
				state: currentCampaign.state,
				storiesPendingApprovalCount:
					currentCampaign.storiesPendingApprovalCount,
				storiesPendingApproval: campaignToUpdate
					? campaignToUpdate.storiesPendingApproval
					: state.campaigns[currentCampaign._id]?.storiesPendingApproval,
			};
		});
	}

	// Remove approved stories from state.pendingStories
	state.allPendingStoriesList = state.allPendingStoriesList.filter(
		(story: any) => !stories.includes(story._id),
	);
	state.allPendingStoriesPagination.count -= stories.length;

	if (!bulkAction) {
		state.pendingStoriesByCampaign = state.pendingStoriesByCampaign.filter(
			(story: any) => !stories.includes(story._id),
		);
		state.pendingStoriesPaginationByCampaign.count -= stories.length;
	}
}

function rejectPendingStoriesMapper(
	state: campaignsStateType,
	action: ResponseAction,
) {
	const {
		response: { campaigns, stories },
		payload: { campaignToUpdate, campaignReportsPage, bulkAction },
	} = action;

	// Use the first campaign for campaignReportsPage
	if (campaignReportsPage && campaigns && campaigns.length > 0) {
		const campaign = campaigns[0];
		state.campaign = {
			...state.campaign,
			storiesPendingApproval: campaignToUpdate.storiesPendingApproval,
			storiesPendingApprovalCount: campaignToUpdate.storiesPendingApprovalCount,
			actionsRequired: campaign.actionsRequired,
			assignedStories: campaignToUpdate.assignedStories,
			assignedStoriesCount: campaign.assignedStoriesCount,
		};
	}

	// Update all campaigns in the campaigns array
	const shouldUpdateAllCampaigns =
		!campaignReportsPage && campaigns && campaigns.length > 0;
	if (shouldUpdateAllCampaigns) {
		campaigns.forEach((currentCampaign: any) => {
			state.campaigns[currentCampaign._id] = {
				...state.campaigns[currentCampaign._id],
				actionsRequired: currentCampaign.actionsRequired,
				assignedStoriesCount: currentCampaign.assignedStoriesCount,
				completedStoriesCount: currentCampaign.completedStoriesCount,
				isLive: currentCampaign.isLive,
				state: currentCampaign.state,
				storiesPendingApprovalCount:
					currentCampaign.storiesPendingApprovalCount,
				assignedStories: currentCampaign.assignedStories,
				storiesPendingApproval: campaignToUpdate
					? campaignToUpdate.storiesPendingApproval
					: state.campaigns[currentCampaign._id]?.storiesPendingApproval,
			};
		});
	}

	// Remove rejected stories from state.pendingStories
	state.allPendingStoriesList = state.allPendingStoriesList.filter(
		(story: any) => !stories.includes(story._id),
	);
	state.allPendingStoriesPagination.count -= stories.length;

	if (!bulkAction) {
		state.pendingStoriesByCampaign = state.pendingStoriesByCampaign.filter(
			(story: any) => !stories.includes(story._id),
		);
		state.pendingStoriesPaginationByCampaign.count -= stories.length;
	}
}

function deletePendingStoriesMapper(
	state: campaignsStateType,
	action: ResponseAction,
) {
	const {
		response: { campaigns, stories },
		payload: { campaignToUpdate, campaignReportsPage },
	} = action;

	const campaign = campaigns && campaigns.length > 0 && campaigns[0];
	state.assignedStoriesByCampaign = state.assignedStoriesByCampaign.filter(
		(story: any) => !stories.includes(story._id),
	);
	if (campaignReportsPage) {
		state.campaign = {
			...state.campaign,
			assignedStories: campaignToUpdate.assignedStories,
			assignedStoriesCount: campaignToUpdate.assignedStoriesCount,
			actionsRequired: campaign.actionsRequired,
		};
	} else {
		state.campaigns[campaign._id] = {
			...campaignToUpdate,
			actionsRequired: campaign.actionsRequired,
		};
	}
}

function verifiedStorytellersMapper(
	state: recipesStateType,
	action: ResponseAction,
) {
	const {
		response: { sendingAddresses },
	} = action;
	state.verifiedStorytellers = sendingAddresses;
}

function setCampaignLiveSuccessMapper(
	state: recipesStateType,
	action: ResponseAction,
) {
	const {
		response: { campaign },
		payload: { removeFromCampaignsList },
	} = action;

	state.campaigns[campaign._id] = campaign;

	if (removeFromCampaignsList) {
		//TODO: Fix this state mutation
		// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
		delete state.campaigns[campaign._id];
	}
}

function setCampaignLiveErrorMapper(
	state: recipesStateType,
	action: ResponseAction,
) {
	const { error } = action;
	const errorData = error?.response?.data;
	const errorCode = (errorData?.errorCode as number) || 0;

	const errorMessage: { [key: number]: string } = {
		1904: ToastMessage.setReelsLiveNoStopsError,
		0: ToastMessage.error,
	};

	if (errorCode === EXPIRATION_DATE_BEFORE_SCHEDULE_DATE_ERROR_CODE) {
		// Skipping the toast message here to be handled in CampaignsTable
		// To set a new expiration date
		return;
	}

	showToast({
		message:
			errorCode && errorMessage[errorCode]
				? errorMessage[errorCode]
				: ToastMessage.error,
		type: 'error',
	});
}

function updateCampaignV2Mapper(
	state: recipesStateType,
	action: ResponseAction,
) {
	const {
		response: { campaign, stories },
		payload: { isReelsCampaign, recorderAssignedTo, isCampaignReportPage },
	} = action;

	const story = stories[0];

	const storyIndexInPendingStories = state.assignedStoriesByCampaign.findIndex(
		(assignedStory: any) => assignedStory?.id === story?.id,
	);

	if (storyIndexInPendingStories !== -1) {
		state.assignedStoriesByCampaign[storyIndexInPendingStories].assignedTo =
			recorderAssignedTo;
		state.assignedStoriesByCampaign[storyIndexInPendingStories].updatedAt =
			story.updatedAt;
	}

	if (isCampaignReportPage) {
		// For reel campaigns, assignedStory may return not populated
		const storyIndex = state.campaign.assignedStories.findIndex(
			(assignedStory: any) =>
				typeof assignedStory !== 'string' && assignedStory?._id === story?._id,
		);
		if (storyIndex !== -1) {
			state.campaign.assignedStories[storyIndex].assignedTo =
				recorderAssignedTo;
			state.campaign.assignedStories[storyIndex].updatedAt = story.updatedAt;
		}
		// Only update if storytellers are populated
		if (typeof campaign.storytellers?.[0] === 'object') {
			state.campaign.storytellers = campaign.storytellers;
		}
		if (!!campaign.expirationData) {
			state.campaign.expirationData = campaign.expirationData;
		}
	} else {
		if (recorderAssignedTo?._id) {
			const storyIndex = state.campaigns[
				campaign._id
			].assignedStories.findIndex(
				(assignedStory: any) => assignedStory._id === story._id,
			);

			if (storyIndex !== -1) {
				state.campaigns[campaign._id].assignedStories[storyIndex].assignedTo =
					recorderAssignedTo;
				state.campaigns[campaign._id].assignedStories[storyIndex].updatedAt =
					story.updatedAt;
			}
		}
		if (state.campaigns[campaign._id] && !isReelsCampaign) {
			state.campaigns[campaign._id].instructions = campaign.instructions;
		}
		if (state.campaigns[campaign._id] && campaign.expirationData) {
			state.campaigns[campaign._id].expirationData = campaign.expirationData;
		}
		state.campaigns[campaign._id].storytellers = campaign.storytellers;
	}
}

function updateNicheInboxContentTextMapper(
	state: campaignsStateType,
	action: ResponseAction,
) {
	const campaignId =
		action.response?.campaign?._id ?? action.payload?.campaignId;
	const text =
		action.response?.campaign?.nicheInboxMessageContent?.text ??
		action.payload?.text;

	if (!campaignId || text === undefined) return;

	if (state.campaign?._id === campaignId) {
		state.campaign.nicheInboxMessageContent = {
			...state.campaign.nicheInboxMessageContent,
			text,
		};
	}

	if (state.campaigns?.[campaignId]) {
		state.campaigns[campaignId].nicheInboxMessageContent = {
			...state.campaigns[campaignId].nicheInboxMessageContent,
			text,
		};
	}
}

const campaignSlice = createSlice({
	name: 'campaign',
	initialState,
	reducers: {
		RESET(state: campaignsStateType) {
			state.campaignCount = 0;
			state.campaigns = {};
			state.savedCampaign = initialState.savedCampaign;
			state.campaignName = '';
			state = initialState;
		},
		RESET_CUSTOM_STATE(state: campaignsStateType) {
			state.custom = initialState.custom;
		},
		RESET_BUILDING_CAMPAIGN_STATE(state: campaignsStateType) {
			state.savedCampaign = initialState.savedCampaign;
			state.campaignName = '';
		},
		RESET_CAMPAIGNS_LIST_STATE(state: campaignsStateType) {
			state.campaigns = {};
			state.campaignCount = 0;
		},
		RESET_RECENTLY_LAUNCHED_CAMPAIGNS(state: campaignsStateType) {
			state.recentlyLaunchedCampaigns = [];
		},
		RESET_ASSIGNED_PENDING_STORIES_BY_CAMPAIGN(state: campaignsStateType) {
			state.pendingStoriesByCampaign = [];
			state.pendingStoriesPaginationByCampaign = {
				currentPage: FIRST_PAGE,
				totalPages: null,
				count: 0,
			};

			state.assignedStoriesByCampaign = [];
			state.assignedStoriesPaginationByCampaign = {
				currentPage: FIRST_PAGE,
				totalPages: null,
				count: 0,
			};
		},
		EDIT_CAMPAIGN(state: campaignsStateType, action: ResponseAction) {
			state.savedCampaign = {
				...action.payload.campaign,
				isDraft: !!action.payload.campaign._id,
				activeCampaignEditMode: action.payload.activeCampaignEditMode,
			};
			state.campaignName = action.payload?.campaign.name;
		},
		SET_CAMPAIGN_TREE(state: campaignsStateType, action: ResponseAction) {
			const { payload } = action;
			state.campaignTree = normalizeCampaignTree(payload);
		},
		SET_CAMPAIGN_NAME(state: campaignsStateType, action: { payload: string }) {
			state.campaignName = action.payload;
		},
		RESET_ACTIVE_CTAS(state: campaignsStateType) {
			state.activeCtas.activeCTACount = 0;
			state = initialState;
		},
		UPDATE_CAMPAIGN_CTA(
			state: campaignsStateType,
			action: {
				payload: {
					campaignId: string;
					ctaId: string;
					messageType: 'dueDateNudge';
					text: string;
				};
			},
		) {
			const { campaignId, ctaId, messageType, text } = action.payload;
			const campaign = state.campaign;

			if (campaign?._id === campaignId && campaign?.callToActions) {
				const ctaIndex = campaign.callToActions.findIndex(
					(c: any) => c._id === ctaId,
				);
				if (ctaIndex !== -1 && campaign.callToActions[ctaIndex][messageType]) {
					campaign.callToActions[ctaIndex][messageType].text = text;
				}
			}
		},
		UPDATE_CAMPAIGN_CTA_HIDDEN(
			state: campaignsStateType,
			action: {
				payload: {
					campaignId: string;
					updates: CtaHiddenUpdate[];
				};
			},
		) {
			const { campaignId, updates } = action.payload;
			const campaign = state.campaign;

			if (campaign?._id === campaignId && campaign?.callToActions) {
				updates.forEach(({ ctaId, hidden }) => {
					const ctaIndex = campaign.callToActions.findIndex(
						(c: any) => c._id === ctaId,
					);
					if (ctaIndex !== -1) {
						campaign.callToActions[ctaIndex].hidden = hidden;
					}
				});
			}
		},
	},
	extraReducers: {
		...createFetchReducer(getCampaignTypes, getCampaign),
		...createFetchReducer(getCampaignsCountTypes, getCampaignsCountMapper),
		...createFetchReducer(filterCampaignsTypes, paginationMapper),

		...createFetchReducer(launchDraftCampaignTypes, launchDraftCampaignMapper),

		...createFetchReducer(
			updateCampaignTypes,
			updateCampaignMapper,
			updateCampaignErrorMapper,
		),
		...createFetchReducer(pinCampaignTypes, pinCampaignMapper),

		...createFetchReducer(deactivateCampaignTypes, deactivateCampaignMapper),
		...createFetchReducer(activateCampaignTypes, activateCampaignMapper),
		...createFetchReducer(exportCampaignsTypes, exportCampaignsSuccessMapper),
		...createFetchReducer(
			exportSingleCampaignTypes,
			exportSingleCampaignSuccessMapper,
		),
		...createFetchReducer(
			addContactsToCampaignTypes,
			() => {},
			addContactsToCampaignErrorMapper,
		),
		...createFetchReducer(acceptPendingCampaignContactsTypes),
		...createFetchReducer(emailPreviewTypes, () => {}, emailPreviewErrorMapper),
		...createFetchReducer(getEmailTemplatesTypes),
		...createFetchReducer(getEmailTemplateHTMLTypes),
		...createFetchReducer(editEmailTemplateTypes),
		...createFetchReducer(
			getVerifiedStorytellersTypes,
			verifiedStorytellersMapper,
		),
		...createFetchReducer(resendStoriesCounttypes),
		...createFetchReducer(resendCampaignStoriesTypes),
		...createFetchReducer(getCampaignOptionsTypes, optionsMapper),
		...createFetchReducer(
			saveCampaignTypes,
			saveCampaignMapper,
			saveCampaignErrorMapper,
		),
		...createFetchReducer(
			launchCampaignTypes,
			launchCampaignMapper,
			launchCampaignErrorMapper,
		),
		...createFetchReducer(
			saveCurrentChildCampaignTypes,
			saveCurrentChildCampaignMapper,
		),
		...createFetchReducer(
			saveAndUpdateCurrentChildCampaignTypes,
			saveCampaignMapper,
		),
		...createFetchReducer(duplicateCampaignTypes),
		...createFetchReducer(
			bulkDeleteDraftCampaignsTypes,
			bulkDeleteDraftCampaignsMapper,
		),
		...createFetchReducer(getHotglueJobTypes),
		...createFetchReducer(getCampaignPerCTAType),
		...createFetchReducer(getCTAsInCampaignsTypes, activeCTAsPaginationMapper),

		...createFetchReducer(getPendingStoriesTypes, getPendingStoriesMapper),
		...createFetchReducer(
			approvePendingStoriesTypes,
			approvePendingStoriesMapper,
		),
		...createFetchReducer(getAssignedStoriesTypes, getAssignedStoriesMapper),
		...createFetchReducer(
			rejectPendingStoriesTypes,
			rejectPendingStoriesMapper,
		),
		...createFetchReducer(
			deletePendingStoriesTypes,
			deletePendingStoriesMapper,
		),
		...createFetchReducer(
			setLiveCampaignTypes,
			setCampaignLiveSuccessMapper,
			setCampaignLiveErrorMapper,
		),
		...createFetchReducer(updateCampaignV2Types, updateCampaignV2Mapper),
		...createFetchReducer(updateCampaignStorytellersTypes),
		...createFetchReducer(validateRepeatedNameTypes),
		...createFetchReducer(updateCampaignEmailFieldsTypes),
		...createFetchReducer(
			updateNicheInboxContentTextTypes,
			updateNicheInboxContentTextMapper,
		),
	},
});

export default campaignSlice.reducer;
